<script setup lang="ts">
import type { Session } from '@/types'
import { Head, usePage } from '@inertiajs/vue3'

import { computed } from 'vue'
import SettingsCard from '@/components/settings/SettingsCard.vue'
import SettingsPanel from '@/components/settings/SettingsPanel.vue'
import { dashboardLayout } from '@/lib/layouts'
import { sessionPath } from '@/routes'

interface Props {
  sessions: Session[]
}

defineOptions({
  layout: dashboardLayout,
})

const props = defineProps<Props>()

const { auth } = usePage().props

const sessionCountLabel = computed(() =>
  props.sessions.length === 1
    ? '1 active session'
    : `${props.sessions.length} active sessions`,
)

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}
</script>

<template>
  <Head title="Sessions" />

  <SettingsPanel
    title="Settings"
    description="Manage profile, security, sessions, and appearance preferences."
  >
    <SettingsCard
      title="Active sessions"
      description="Review signed-in devices and revoke access when needed."
      icon="i-lucide-monitor-dot"
    >
      <div class="mb-4 flex items-center justify-between gap-3">
        <UBadge color="neutral" variant="subtle" icon="i-lucide-circle">
          {{ sessionCountLabel }}
        </UBadge>
      </div>

      <div
        class="divide-default border-default overflow-hidden rounded-md border"
      >
        <div
          v-for="session in sessions"
          :key="session.id"
          class="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="min-w-0 space-y-2">
            <div class="flex min-w-0 flex-wrap items-center gap-2">
              <UIcon
                name="i-lucide-monitor"
                class="text-muted size-4 shrink-0"
              />
              <p class="text-highlighted min-w-0 truncate text-sm font-medium">
                {{ session.user_agent }}
              </p>
              <UBadge
                v-if="session.id === auth.session.id"
                color="success"
                variant="subtle"
                size="sm"
              >
                Current
              </UBadge>
            </div>

            <div class="text-muted flex flex-wrap gap-x-4 gap-y-1 text-sm">
              <span class="inline-flex items-center gap-1.5">
                <UIcon name="i-lucide-network" class="size-4" />
                {{ session.ip_address }}
              </span>
              <span class="inline-flex items-center gap-1.5">
                <UIcon name="i-lucide-clock" class="size-4" />
                {{ formatDateTime(session.created_at) }}
              </span>
            </div>
          </div>

          <UButton
            v-if="session.id !== auth.session.id"
            :href="sessionPath({ id: session.id })"
            method="delete"
            as="button"
            color="error"
            variant="soft"
            icon="i-lucide-log-out"
            label="Log out"
            class="self-start sm:self-center"
          />
        </div>
      </div>
    </SettingsCard>
  </SettingsPanel>
</template>
